/**
 * useFlowDiagram.js
 *
 * React hook that provides real-time Firestore-backed flow diagram data for
 * the UNS architecture diagram components (UnsFlow, UnsFlowNode).
 *
 * The hook drives the live MQTT topic-highlighting pipeline:
 *
 *   Firestore (unsFlowData/{factoryId})
 *       ↓  onSnapshot
 *   useFlowDiagram({ factoryId })
 *       ↓  { flowNodes, backgroundBoxes, diagram }
 *   UnsFlow  ← also receives hotTopic from Uns.js via MQTT
 *       ↓
 *   Highlighted edges / nodes
 *
 * Usage
 * ─────────────────────────────────────────────────────────────────────────────
 *   // Subscribe to a single factory diagram (live-updating)
 *   const { diagram, flowNodes, backgroundBoxes, loading, error } =
 *     useFlowDiagram({ factoryId: 'tHktnIRtcSYdmjTa0CZe' });
 *
 *   // Subscribe to ALL diagrams (admin editor)
 *   const { diagrams, loading, error } = useFlowDiagram({ all: true });
 */

import { useState, useEffect, useCallback } from 'react';
import {
  subscribeToFlowDiagram,
  subscribeToAllFlowDiagrams,
  createFlowDiagram,
  updateFlowDiagram,
  upsertFlowDiagram,
  replaceFlowNodes,
  updateFlowNode,
  deleteFlowNode,
  updateBackgroundBoxes,
  addBackgroundBox,
  updateBackgroundBox,
  deleteBackgroundBox,
  deleteFlowDiagram,
  seedFlowDiagrams,
} from '../services/flowDiagramService';

// ─── Single-diagram hook ─────────────────────────────────────────────────────

/**
 * Subscribe to a single flow diagram by factoryId.
 *
 * @param {string} factoryId
 * @returns {{
 *   diagram        : object | null,
 *   flowNodes      : object[],
 *   backgroundBoxes: object[],
 *   loading        : boolean,
 *   error          : string | null,
 * }}
 */
export const useFlowDiagram = (factoryId) => {
  const [diagram, setDiagram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!factoryId) {
      setDiagram(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToFlowDiagram(
      factoryId,
      (data) => {
        setDiagram(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load flow diagram');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [factoryId]);

  return {
    diagram,
    // Convenience accessors — keeps callers from needing to check nulls
    flowNodes: diagram?.flow ?? [],
    backgroundBoxes: diagram?.backgroundBoxes ?? [],
    loading,
    error,
  };
};

// ─── All-diagrams hook (admin editor) ───────────────────────────────────────

/**
 * Subscribe to all flow diagrams in Firestore.
 * Intended for the admin architecture diagram editor.
 *
 * @returns {{
 *   diagrams: object[],
 *   loading : boolean,
 *   error   : string | null,
 * }}
 */
export const useAllFlowDiagrams = () => {
  const [diagrams, setDiagrams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToAllFlowDiagrams(
      (data) => {
        setDiagrams(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load flow diagrams');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { diagrams, loading, error };
};

// ─── Mutation hook (admin editor writes) ────────────────────────────────────

/**
 * Provides all Firestore write operations for flow diagrams.
 * Intended for the admin architecture diagram editor.
 *
 * @param {string} [factoryId]  — optional; pre-binds mutations to this factory
 * @param {string} [userId]     — uid of the authenticated admin
 * @returns {object} mutation helpers
 */
export const useFlowDiagramMutations = (factoryId, userId) => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);

  /** Wrap an async mutation with loading/error state */
  const withState = useCallback(async (fn) => {
    setIsPending(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err.message || 'Operation failed');
      console.error('[useFlowDiagramMutations]', err);
    } finally {
      setIsPending(false);
    }
  }, []);

  // ── Diagram-level mutations ──────────────────────────────────────────────

  const saveDiagram = useCallback(
    (diagramData, fid = factoryId) =>
      withState(() => createFlowDiagram(fid, diagramData, userId)),
    [withState, factoryId, userId]
  );

  const patchDiagram = useCallback(
    (updates, fid = factoryId) =>
      withState(() => updateFlowDiagram(fid, updates, userId)),
    [withState, factoryId, userId]
  );

  /**
   * Create the diagram if it does not exist, or merge updates into it if it does.
   * Mirrors the setDoc({ merge: true }) pattern used for unsConfig writes.
   * Safe to call on first load from the admin editor.
   */
  const upsertDiagram = useCallback(
    (diagramData, fid = factoryId) =>
      withState(() => upsertFlowDiagram(fid, diagramData, userId)),
    [withState, factoryId, userId]
  );

  const removeDiagram = useCallback(
    (fid = factoryId) => withState(() => deleteFlowDiagram(fid)),
    [withState, factoryId]
  );

  // ── Node-level mutations ─────────────────────────────────────────────────

  /**
   * Replace the entire flow node array (e.g. after a drag-and-drop reposition).
   */
  const saveNodes = useCallback(
    (flowNodes, fid = factoryId) =>
      withState(() => replaceFlowNodes(fid, flowNodes, userId)),
    [withState, factoryId, userId]
  );

  /**
   * Update a single node by its id.
   */
  const saveNode = useCallback(
    (nodeId, nodeUpdate, fid = factoryId) =>
      withState(() => updateFlowNode(fid, nodeId, nodeUpdate, userId)),
    [withState, factoryId, userId]
  );

  /**
   * Remove a single node by its id.
   */
  const removeNode = useCallback(
    (nodeId, fid = factoryId) =>
      withState(() => deleteFlowNode(fid, nodeId, userId)),
    [withState, factoryId, userId]
  );

  // ── Background box mutations ─────────────────────────────────────────────

  /**
   * Replace the entire backgroundBoxes array (bulk replacement).
   * Use when saving the full admin editor state.
   */
  const saveBackgroundBoxes = useCallback(
    (boxes, fid = factoryId) =>
      withState(() => updateBackgroundBoxes(fid, boxes, userId)),
    [withState, factoryId, userId]
  );

  /**
   * Append a single new background box by its id.
   * Mirrors the per-item node operations for consistent CRUD surface.
   */
  const addBox = useCallback(
    (boxData, fid = factoryId) =>
      withState(() => addBackgroundBox(fid, boxData, userId)),
    [withState, factoryId, userId]
  );

  /**
   * Update a single background box by its id.
   */
  const saveBox = useCallback(
    (boxId, boxUpdate, fid = factoryId) =>
      withState(() => updateBackgroundBox(fid, boxId, boxUpdate, userId)),
    [withState, factoryId, userId]
  );

  /**
   * Remove a single background box by its id.
   */
  const removeBox = useCallback(
    (boxId, fid = factoryId) =>
      withState(() => deleteBackgroundBox(fid, boxId, userId)),
    [withState, factoryId, userId]
  );

  // ── Migration helper ─────────────────────────────────────────────────────

  /**
   * Seed Firestore with the existing hard-coded flowDataArray.
   * Call this once from the admin panel during migration.
   */
  const seedDiagrams = useCallback(
    (diagrams) => withState(() => seedFlowDiagrams(diagrams, userId)),
    [withState, userId]
  );

  return {
    isPending,
    error,
    // Diagram-level
    saveDiagram,
    patchDiagram,
    upsertDiagram,
    removeDiagram,
    // Node-level
    saveNodes,
    saveNode,
    removeNode,
    // Background boxes — bulk
    saveBackgroundBoxes,
    // Background boxes — individual (mirrors node-level operations)
    addBox,
    saveBox,
    removeBox,
    // Migration
    seedDiagrams,
  };
};

// Default export: the most commonly used hook
export default useFlowDiagram;
